import { supabase } from '../lib/supabase';

// Fetch report data with role-based access
export const fetchReportData = async (reportType, filters = {}, user = null, userProfile = null) => {
  try {
    // Use provided user or fallback to fetching current user
    let currentUser = user;
    let currentProfile = userProfile;
    
    if (!currentUser) {
      const { data: { user: fetchedUser }, error: userError } = await supabase?.auth?.getUser();
      if (userError || !fetchedUser) {
        throw new Error('User not authenticated');
      }
      currentUser = fetchedUser;
    }

    // Get user profile if not provided
    if (!currentProfile && currentUser) {
      const { data: profile, error: profileError } = await supabase
        ?.from('user_profiles')
        ?.select('role')
        ?.eq('id', currentUser?.id)
        ?.single();

      if (profileError) {
        throw profileError;
      }
      currentProfile = profile;
    }

    const userRole = currentProfile?.role;
    let query;

    // Build query based on report type
    if (reportType === 'players') {
      query = supabase?.from('players')?.select('*');
      
      // Role-based filtering is handled by RLS policies
      // Scouts and clubs can see all, players see only their own
      
    } else if (reportType === 'coaches') {
      query = supabase?.from('coaches')?.select('*');
      
      // Role-based filtering is handled by RLS policies
      // Scouts and clubs can see all, coaches see only their own
      
    } else if (reportType === 'goalkeepers') {
      query = supabase?.from('goalkeepers')?.select('*');
      
      // Role-based filtering is handled by RLS policies
      // Scouts and clubs can see all, players (goalkeepers) see only their own
    }

    // Apply filters
    if (filters?.position && filters?.position !== 'all' && reportType === 'players') {
      query = query?.eq('position', filters?.position);
    }

    if (filters?.league && filters?.league !== 'all') {
      query = query?.ilike('competitions', `%${filters?.league}%`);
    }

    // Timeframe filtering (created_at)
    if (filters?.timeframe && filters?.timeframe !== 'all') {
      const now = new Date();
      let startDate;

      switch (filters?.timeframe) {
        case '7days':
          startDate = new Date(now?.getTime() - 7 * 24 * 60 * 60 * 1000);
          break;
        case '30days':
          startDate = new Date(now?.getTime() - 30 * 24 * 60 * 60 * 1000);
          break;
        case '90days':
          startDate = new Date(now?.getTime() - 90 * 24 * 60 * 60 * 1000);
          break;
        case '1year':
          startDate = new Date(now?.getTime() - 365 * 24 * 60 * 60 * 1000);
          break;
        default:
          startDate = null;
      }

      if (startDate) {
        query = query?.gte('created_at', startDate?.toISOString());
      }
    }

    // Execute query
    const { data, error } = await query?.order('created_at', { ascending: false });

    if (error) {
      throw error;
    }

    // Convert snake_case to camelCase
    const camelCaseData = data?.map(record => ({
      id: record?.id,
      userId: record?.user_id,
      fullName: record?.full_name,
      age: record?.age,
      dateOfBirth: record?.date_of_birth,
      nationality: record?.nationality,
      height: record?.height,
      position: record?.position,
      preferredFoot: record?.preferred_foot,
      previousTeams: record?.previous_teams,
      currentTeam: record?.current_team,
      presentClub: record?.present_club,
      competitions: record?.competitions,
      competitionsManaged: record?.competitions_managed,
      totalAppearances: record?.total_appearances,
      gamesStarted: record?.games_started,
      gamesSubstitute: record?.games_substitute,
      totalMinutes: record?.total_minutes,
      goalsScored: record?.goals_scored,
      assists: record?.assists,
      motmAwards: record?.motm_awards,
      yellowCards: record?.yellow_cards,
      redCards: record?.red_cards,
      availableForTransfer: record?.available_for_transfer,
      marketValue: record?.market_value,
      contractStatus: record?.contract_status,
      releaseClause: record?.release_clause,
      agentName: record?.agent_name,
      agentEmail: record?.agent_email,
      agentPhone: record?.agent_phone,
      nationalTeam: record?.national_team,
      juniorLevel: record?.junior_level,
      seniorTeam: record?.senior_team,
      internationalCaps: record?.international_caps,
      internationalGoals: record?.international_goals,
      playerPhoto: record?.player_photo,
      coachPhoto: record?.coach_photo,
      goalkeeperPhoto: record?.goalkeeper_photo,
      matchFootage: record?.match_footage,
      matchHighlights: record?.match_highlights,
      medicalReports: record?.medical_reports,
      certificates: record?.certificates,
      coachingLicense: record?.coaching_license,
      preferredFormation: record?.preferred_formation,
      coachingStyle: record?.coaching_style,
      totalGamesManaged: record?.total_games_managed,
      gamesWon: record?.games_won,
      gamesDrawn: record?.games_drawn,
      gamesLost: record?.games_lost,
      gamesPlayed: record?.games_played,
      cleanSheets: record?.clean_sheets,
      goalsConceded: record?.goals_conceded,
      minutesPlayed: record?.minutes_played,
      savesMade: record?.saves_made,
      penaltiesSaved: record?.penalties_saved,
      cupsWon: record?.cups_won,
      leagueTitles: record?.league_titles,
      individualAwards: record?.individual_awards,
      nationalTeamManaged: record?.national_team_managed,
      nationalTeamLevel: record?.national_team_level,
      createdAt: record?.created_at,
      updatedAt: record?.updated_at
    }));

    return { data: camelCaseData, error: null };
  } catch (error) {
    console.error('Error fetching report data:', error);
    return { data: [], error: error?.message };
  }
};

// Generate PDF report
export const generatePDFReport = async (reportType, data, filters) => {
  try {
    const { jsPDF } = await import('jspdf');
    await import('jspdf-autotable');

    const doc = new jsPDF();
    const pageWidth = doc?.internal?.pageSize?.getWidth();

    // Title
    doc?.setFontSize(18);
    doc?.setFont(undefined, 'bold');
    doc?.text(`${reportType?.charAt(0)?.toUpperCase() + reportType?.slice(1)} Performance Report`, pageWidth / 2, 20, { align: 'center' });

    // Filters info
    doc?.setFontSize(10);
    doc?.setFont(undefined, 'normal');
    let yPos = 30;
    doc?.text(`Position: ${filters?.position || 'All'}`, 14, yPos);
    yPos += 6;
    doc?.text(`League: ${filters?.league || 'All'}`, 14, yPos);
    yPos += 6;
    doc?.text(`Timeframe: ${filters?.timeframe || 'All time'}`, 14, yPos);
    yPos += 6;
    doc?.text(`Generated: ${new Date()?.toLocaleDateString()}`, 14, yPos);
    yPos += 10;

    // Table data
    let columns = [];
    let rows = [];

    if (reportType === 'players') {
      columns = ['Name', 'Position', 'Team', 'Goals', 'Assists', 'Apps'];
      rows = data?.map(player => [
        player?.fullName || 'N/A',
        player?.position || 'N/A',
        player?.currentTeam || 'N/A',
        player?.goalsScored || 0,
        player?.assists || 0,
        player?.totalAppearances || 0
      ]);
    } else if (reportType === 'coaches') {
      columns = ['Name', 'License', 'Club', 'Games', 'Wins', 'Win %'];
      rows = data?.map(coach => [
        coach?.fullName || 'N/A',
        coach?.coachingLicense || 'N/A',
        coach?.presentClub || 'N/A',
        coach?.totalGamesManaged || 0,
        coach?.gamesWon || 0,
        coach?.totalGamesManaged > 0 ? `${((coach?.gamesWon / coach?.totalGamesManaged) * 100)?.toFixed(1)}%` : '0%'
      ]);
    } else if (reportType === 'goalkeepers') {
      columns = ['Name', 'Team', 'Games', 'Clean Sheets', 'Saves', 'Goals Conceded'];
      rows = data?.map(gk => [
        gk?.fullName || 'N/A',
        gk?.currentTeam || 'N/A',
        gk?.gamesPlayed || 0,
        gk?.cleanSheets || 0,
        gk?.savesMade || 0,
        gk?.goalsConceded || 0
      ]);
    }

    doc?.autoTable({
      head: [columns],
      body: rows,
      startY: yPos,
      theme: 'grid',
      headStyles: { fillColor: [59, 130, 246] },
      styles: { fontSize: 9 }
    });

    // Footer
    const pageCount = doc?.internal?.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc?.setPage(i);
      doc?.setFontSize(8);
      doc?.text(
        `Page ${i} of ${pageCount}`,
        pageWidth / 2,
        doc?.internal?.pageSize?.getHeight() - 10,
        { align: 'center' }
      );
    }

    doc?.save(`${reportType}-report-${new Date()?.getTime()}.pdf`);
    return { success: true };
  } catch (error) {
    console.error('Error generating PDF:', error);
    return { success: false, error: error?.message };
  }
};

// Generate CSV report
export const generateCSVReport = async (reportType, data) => {
  try {
    const Papa = await import('papaparse');

    let csvData = [];

    if (reportType === 'players') {
      csvData = data?.map(player => ({
        'Full Name': player?.fullName || '',
        'Age': player?.age || '',
        'Nationality': player?.nationality || '',
        'Position': player?.position || '',
        'Current Team': player?.currentTeam || '',
        'Goals': player?.goalsScored || 0,
        'Assists': player?.assists || 0,
        'Appearances': player?.totalAppearances || 0,
        'Market Value': player?.marketValue || '',
        'Available for Transfer': player?.availableForTransfer ? 'Yes' : 'No'
      }));
    } else if (reportType === 'coaches') {
      csvData = data?.map(coach => ({
        'Full Name': coach?.fullName || '',
        'Nationality': coach?.nationality || '',
        'License': coach?.coachingLicense || '',
        'Current Club': coach?.presentClub || '',
        'Games Managed': coach?.totalGamesManaged || 0,
        'Wins': coach?.gamesWon || 0,
        'Draws': coach?.gamesDrawn || 0,
        'Losses': coach?.gamesLost || 0,
        'Win Percentage': coach?.totalGamesManaged > 0 ? `${((coach?.gamesWon / coach?.totalGamesManaged) * 100)?.toFixed(1)}%` : '0%'
      }));
    } else if (reportType === 'goalkeepers') {
      csvData = data?.map(gk => ({
        'Full Name': gk?.fullName || '',
        'Age': gk?.age || '',
        'Nationality': gk?.nationality || '',
        'Current Team': gk?.currentTeam || '',
        'Games Played': gk?.gamesPlayed || 0,
        'Clean Sheets': gk?.cleanSheets || 0,
        'Saves': gk?.savesMade || 0,
        'Goals Conceded': gk?.goalsConceded || 0,
        'Market Value': gk?.marketValue || '',
        'Available for Transfer': gk?.availableForTransfer ? 'Yes' : 'No'
      }));
    }

    const csv = Papa?.default?.unparse(csvData);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link?.setAttribute('href', url);
    link?.setAttribute('download', `${reportType}-report-${new Date()?.getTime()}.csv`);
    link.style.visibility = 'hidden';
    document.body?.appendChild(link);
    link?.click();
    document.body?.removeChild(link);

    return { success: true };
  } catch (error) {
    console.error('Error generating CSV:', error);
    return { success: false, error: error?.message };
  }
};

// Save scheduled report
export const saveScheduledReport = async (reportConfig) => {
  try {
    const { data: { user }, error: userError } = await supabase?.auth?.getUser();
    if (userError || !user) {
      throw new Error('User not authenticated');
    }

    const { data, error } = await supabase
      ?.from('scheduled_reports')
      ?.insert({
        user_id: user?.id,
        report_name: reportConfig?.reportName,
        report_type: reportConfig?.reportType,
        filters: reportConfig?.filters || {},
        recipients: reportConfig?.recipients || [],
        frequency: reportConfig?.frequency,
        schedule_time: reportConfig?.scheduleTime,
        is_active: true
      })
      ?.select()
      ?.single();

    if (error) {
      throw error;
    }

    return { data, error: null };
  } catch (error) {
    console.error('Error saving scheduled report:', error);
    return { data: null, error: error?.message };
  }
};

// Send report email (calls edge function)
export const sendReportEmail = async (reportId) => {
  try {
    const { data, error } = await supabase?.functions?.invoke('send-report-email', {
      body: { reportId }
    });

    if (error) {
      throw error;
    }

    return { data, error: null };
  } catch (error) {
    console.error('Error sending report email:', error);
    return { data: null, error: error?.message };
  }
};

// Fetch scheduled reports for current user
export const fetchScheduledReports = async () => {
  try {
    const { data: { user }, error: userError } = await supabase?.auth?.getUser();
    if (userError || !user) {
      throw new Error('User not authenticated');
    }

    const { data, error } = await supabase
      ?.from('scheduled_reports')
      ?.select('*')
      ?.eq('user_id', user?.id)
      ?.order('created_at', { ascending: false });

    if (error) {
      throw error;
    }

    // Convert snake_case to camelCase
    const camelCaseData = data?.map(report => ({
      id: report?.id,
      userId: report?.user_id,
      reportName: report?.report_name,
      reportType: report?.report_type,
      filters: report?.filters,
      recipients: report?.recipients,
      frequency: report?.frequency,
      scheduleTime: report?.schedule_time,
      isActive: report?.is_active,
      lastSentAt: report?.last_sent_at,
      createdAt: report?.created_at,
      updatedAt: report?.updated_at
    }));

    return { data: camelCaseData, error: null };
  } catch (error) {
    console.error('Error fetching scheduled reports:', error);
    return { data: [], error: error?.message };
  }
};

// Delete scheduled report
export const deleteScheduledReport = async (reportId) => {
  try {
    const { error } = await supabase
      ?.from('scheduled_reports')
      ?.delete()
      ?.eq('id', reportId);

    if (error) {
      throw error;
    }

    return { error: null };
  } catch (error) {
    console.error('Error deleting scheduled report:', error);
    return { error: error?.message };
  }
};

// Update scheduled report
export const updateScheduledReport = async (reportId, updates) => {
  try {
    const snakeCaseUpdates = {};
    if (updates?.reportName !== undefined) snakeCaseUpdates.report_name = updates?.reportName;
    if (updates?.reportType !== undefined) snakeCaseUpdates.report_type = updates?.reportType;
    if (updates?.filters !== undefined) snakeCaseUpdates.filters = updates?.filters;
    if (updates?.recipients !== undefined) snakeCaseUpdates.recipients = updates?.recipients;
    if (updates?.frequency !== undefined) snakeCaseUpdates.frequency = updates?.frequency;
    if (updates?.scheduleTime !== undefined) snakeCaseUpdates.schedule_time = updates?.scheduleTime;
    if (updates?.isActive !== undefined) snakeCaseUpdates.is_active = updates?.isActive;

    const { data, error } = await supabase
      ?.from('scheduled_reports')
      ?.update(snakeCaseUpdates)
      ?.eq('id', reportId)
      ?.select()
      ?.single();

    if (error) {
      throw error;
    }

    return { data, error: null };
  } catch (error) {
    console.error('Error updating scheduled report:', error);
    return { data: null, error: error?.message };
  }
};
function getScheduledReports(...args) {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: getScheduledReports is not implemented yet.', args);
  return null;
}

export { getScheduledReports };